import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { type ShipmentInvoiceDetail } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentInvoiceDetail";
import { formatMoney } from "@/App/Views/carrierFormatting";

/**
 * An open invoice audit, at the top of the shipment it's about.
 *
 * States the position in one line — what we agreed to pay, what was invoiced,
 * the difference — because a carrier reading "your invoice is under audit" with
 * no numbers has to go and find them.
 *
 * Whose turn it is drives the emphasis. Waiting on the carrier reads as
 * something owed, the same as an open operational issue; waiting on us is
 * informational, so it doesn't nag them for work that's ours.
 *
 * The action is navigation, not a submission. Audits are settled by replying to
 * the email we sent — the portal's job is to make sure that reply is written
 * knowing what we're basing the challenge on.
 */
export function AuditBanner({
  invoice,
  onSeeWhy,
}: {
  invoice: ShipmentInvoiceDetail;
  onSeeWhy: () => void;
}) {
  const waiting = invoice.waitingOnCarrier === true;

  return (
    <Banner
      variant={waiting ? "warning" : "info"}
      title={
        waiting ? "Invoice Under Audit — Over to You" : "Invoice Under Audit"
      }
      actions={
        <Button primary onClick={onSeeWhy}>
          See Why
        </Button>
      }
    >
      <Stack gap={1}>
        <T size="sm">{describeMoney_(invoice)}</T>
        <T size="sm" color="secondary">
          {waiting
            ? "We've written to you about this — reply to that email and we'll pick it up from there."
            : "Nothing needed from you right now. We're working through it and will come back to you."}
        </T>
      </Stack>
    </Banner>
  );
}

// The numbers, when we have both. Before an invoice arrives there's no
// difference to state, so the banner says what it can rather than showing $0.
function describeMoney_(invoice: ShipmentInvoiceDetail): string {
  const quoted = invoice.quotedCad;
  const invoiced = invoice.invoicedCad;
  if (quoted === undefined || invoiced === undefined) {
    return "We're reviewing the invoicing on this shipment.";
  }
  const difference = invoiced - quoted;
  if (difference <= 0) {
    return `Quoted ${formatMoney(quoted)}, invoiced ${formatMoney(invoiced)}.`;
  }
  return `Quoted ${formatMoney(quoted)}, invoiced ${formatMoney(invoiced)} — ${formatMoney(difference)} above what we agreed.`;
}
